import React from "react";
import Box from "components/common/Box";
import Text from "components/common/Text";
import Grid from "components/common/Grid";
import { CircularProgress, useMediaQuery } from "@mui/material";
import BlurWrapper from "components/common/BlurWrapper";
import LockedTableWrapper from "components/common/LockTableWrapper";
import { usePhcTheme } from "../PhcThemeContext";

/**  rating badge token map */
const RATING_STYLES = {
  Positive: { color: "#45AC70", bg: "rgba(69,172,112,0.2)" },
  Negative: { color: "#C40000", bg: "rgba(196,0,0,0.2)" },
  Neutral: { color: "#007EF0", bg: "rgba(0,126,240,0.2)" },
};

const getRatingStyle = (rating, theme) =>
  RATING_STYLES[String(rating ?? "").trim()] || {
    color: theme.textMuted,
    bg: theme.badgeBg,
  };

const MOBILE_CARD_ICONS = {
  portfolioProfiling: "/images/phc-dashboard/perf-param-portfolio.svg",
  risk: "/images/phc-dashboard/perf-param-risk.svg",
  liquidity: "/images/phc-dashboard/perf-param-liquidity.svg",
  performance: "/images/phc-dashboard/perf-param-performance.svg",
};

const MOBILE_CARD_ICON_SIZES = {
  portfolioProfiling: { width: 18, height: 18 },
  risk: { width: 22, height: 19 },
  liquidity: { width: 16, height: 19 },
  performance: { width: 20, height: 12 },
};

const REMARK_ACCENT_COLORS = {
  portfolioProfiling: "#2977EC",
  risk: "#FFB4AB",
  liquidity: "#4ADE80",
  performance: "#2977EC",
};

/** Single row: Parameter | Rating text + badge | Remarks */
const ParameterRow = ({ item, isLast }) => {
  const { theme } = usePhcTheme();
  // API uses "parameter" (singular); old data may use "parameters"
  const paramLabel = item?.parameter || item?.parameters || "—";
  const ratingText = item?.rating_text || item?.ratingText || "";
  const style = getRatingStyle(item?.rating, theme);

  return (
    <Box
      sx={{
        borderBottom: isLast ? "none" : `1px solid rgba(129,129,129,0.3)`,
      }}
    >
      <Grid
        container
        alignItems="center"
        sx={{
          minHeight: 53,
          px: "24px",
          py: "12px",
        }}
      >
        {/* Parameter */}
        <Grid item xs={12} md={4}>
          <Text
            sx={{
              fontSize: "14px",
              lineHeight: "1.34",
              color: theme.textPrimary,
              pr: 1,
            }}
          >
            {paramLabel}
          </Text>
        </Grid>

        {/* Rating text + badge */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
            <Text
              sx={{
                fontSize: "14px",
                color: theme.textPrimary,
                lineHeight: "normal",
              }}
            >
              {ratingText}
            </Text>
            {item?.rating && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: "8px",
                  py: "6px",
                  borderRadius: "30px",
                  bgcolor: style.bg,
                  flexShrink: 0,
                }}
              >
                <Text
                  sx={{
                    fontWeight: 500,
                    fontSize: "12px",
                    color: style.color,
                    whiteSpace: "nowrap",
                    lineHeight: "normal",
                  }}
                >
                  {item.rating}
                </Text>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Remarks */}
        <Grid item xs={12} md={4}>
          <Text
            sx={{
              fontSize: "14px",
              lineHeight: "1.34",
              color: theme.textPrimary,
              pl: { md: 1 },
            }}
          >
            {item?.remarks || "—"}
          </Text>
        </Grid>
      </Grid>
    </Box>
  );
};

/** Convert snake_case / camelCase key to Title Case display label */
const formatCategoryTitle = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const toCategoryKey = (key) => {
  const normalized = String(key || "").replace(/_/g, " ").toLowerCase();
  if (normalized.includes("portfolio") && normalized.includes("profil")) {
    return "portfolioProfiling";
  }
  if (normalized.includes("risk")) return "risk";
  if (normalized.includes("liquid")) return "liquidity";
  if (normalized.includes("perform")) return "performance";
  return key;
};

const getMobileCategoryTitle = (key) => {
  const categoryKey = toCategoryKey(key);
  if (categoryKey === "portfolioProfiling") return "Portfolio Profiling";
  if (categoryKey === "risk") return "Risk";
  if (categoryKey === "liquidity") return "Liquidity";
  if (categoryKey === "performance") return "Performance";
  return formatCategoryTitle(key);
};

/** Category block: title + inner bordered table */
const CategoryCard = ({ title, rows }) => {
  const { theme } = usePhcTheme();
  const filteredRows = Array.isArray(rows)
    ? rows.filter((r) => Object.keys(r).length > 0)
    : [];

  if (!filteredRows.length) return null;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: "20px",
        border: `1px solid ${theme.sectionBorder}`,
        backgroundColor: theme.cardBg,
        boxShadow:
          "0px -6px 6px -1px rgba(0,0,0,0.1), 0px -8px 4px -2px rgba(0,0,0,0.1)",
        overflow: "hidden",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: theme.insetGlow,
        },
      }}
    >
      {/* Category title */}
      <Box sx={{ px: "24px", pt: "24px", pb: "16px" }}>
        <Text
          sx={{
            fontWeight: 600,
            fontSize: "20px",
            color: theme.textPrimary,
            lineHeight: "normal",
            }}
        >
          {formatCategoryTitle(title)}
        </Text>
      </Box>

      {/* Inner bordered table */}
      <Box
        sx={{
          mx: "24px",
          mb: "24px",
          borderRadius: "12px",
          border: `1.125px solid ${theme.sectionBorderSoft || "rgba(129,129,129,0.3)"}`,
          bgcolor: theme.innerTableBg || "rgba(31,31,31,0.2)",
          overflow: "hidden",
          position: "relative",
          zIndex: 1,
        }}
      >
        {filteredRows.map((row, index) => (
          <ParameterRow
            key={index}
            item={row}
            isLast={index === filteredRows.length - 1}
          />
        ))}
      </Box>
    </Box>
  );
};

const MobileParameterCard = ({ title, rows }) => {
  const { theme } = usePhcTheme();
  const filteredRows = Array.isArray(rows)
    ? rows.filter((r) => Object.keys(r).length > 0)
    : [];

  if (!filteredRows.length) return null;

  const categoryKey = toCategoryKey(title);
  const iconSrc = MOBILE_CARD_ICONS[categoryKey];
  const iconSize = MOBILE_CARD_ICON_SIZES[categoryKey] || {
    width: 18,
    height: 18,
  };
  const remarksColor = REMARK_ACCENT_COLORS[categoryKey] || "#2977EC";
  const remarks =
    filteredRows.find((row) => row?.remarks)?.remarks || "—";

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "12px",
        border: theme.mobileMetaBorder,
        bgcolor: theme.mobileCardBg,
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        p: "25px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Text
          sx={{
            fontWeight: 400,
            fontSize: 16,
            color: "#2977EC",
            lineHeight: "24px",
          }}
        >
          {getMobileCategoryTitle(title)}
        </Text>
        {iconSrc && (
          <Box
            component="img"
            src={iconSrc}
            alt=""
            sx={{
              width: iconSize.width,
              height: iconSize.height,
              flexShrink: 0,
              display: "block",
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          width: "100%",
          pb: "12px",
        }}
      >
        {filteredRows.map((item, index) => {
          const paramLabel = item?.parameter || item?.parameters || "—";
          const style = getRatingStyle(item?.rating, theme);

          return (
            <Box
              key={`${title}-${paramLabel}-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                width: "100%",
              }}
            >
              <Text
                sx={{
                  fontWeight: 500,
                  fontSize: 16,
                  color: theme.mobileLabelColor,
                  lineHeight: "24px",
                  textTransform: "uppercase",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {paramLabel}
              </Text>
              {item?.rating && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: "9.75px",
                    py: "3.75px",
                    borderRadius: "7499.25px",
                    bgcolor: style.bg,
                    border: `0.75px solid ${style.bg.replace(/0\.2\)$/, "0.3)")}`,
                    flexShrink: 0,
                  }}
                >
                  <Text
                    sx={{
                      fontWeight: 400,
                      fontSize: 12,
                      color: style.color,
                      whiteSpace: "nowrap",
                      lineHeight: "18px",
                    }}
                  >
                    {item.rating}
                  </Text>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          width: "100%",
          borderTop: `1px solid ${theme.mobileMetricBorder}`,
          pt: "13px",
        }}
      >
        <Text
          sx={{
            fontWeight: 500,
            fontSize: 16,
            color: remarksColor,
            lineHeight: "26px",
          }}
        >
          <Box component="span" sx={{ color: remarksColor }}>
            REMARKS:
          </Box>{" "}
          <Box component="span" sx={{ color: theme.mobileLabelColor, fontWeight: 400 }}>
            {remarks}
          </Box>
        </Text>
      </Box>
    </Box>
  );
};

const ParameterRating = ({ data, isLoading, componentState }) => {
  const { theme } = usePhcTheme();
  const isMobile = useMediaQuery("(max-width:600px)");
  const shouldShowOverlay =
    componentState === "blur" || componentState === "locked";
  const OverlayComponent =
    componentState === "blur" ? BlurWrapper : LockedTableWrapper;

  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 360,
        }}
      >
        <CircularProgress sx={{ color: "#007EF0" }} />
      </Box>
    );
  }

  const categories = Object.keys(data);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: { xs: 0, md: "20px" },
        border: { xs: "none", md: `1px solid ${theme.sectionBorder}` },
        backgroundColor: { xs: "transparent", md: theme.cardBg },
        boxShadow:
          { xs: "none", md: "0px -6px 6px -1px rgba(0,0,0,0.1), 0px -8px 4px -2px rgba(0,0,0,0.1)" },
        overflow: "hidden",
        px: { xs: 0, md: "28px" },
        py: { xs: 0, md: "42px" },
        "&::after": {
          content: { xs: "none", md: '""' },
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: theme.sectionInsetGlow,
        },
      }}
    >
      {/* Column header bar */}
      {!isMobile && (
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            bgcolor: theme.tableHeaderBg,
            borderRadius: "8px",
            mb: { xs: 2.5, md: "46px" },
            px: "24px",
            py: "16px",
          }}
        >
          <Grid container>
            <Grid item xs={4}>
              <Text
                sx={{
                  fontWeight: 600,
                  fontSize: "20px",
                  color: theme.textPrimary,
                  letterSpacing: "-0.6px",
                  lineHeight: "none",
                  whiteSpace: "nowrap",
                }}
              >
                Parameters
              </Text>
            </Grid>
            <Grid item xs={4}>
              <Text
                sx={{
                  fontWeight: 600,
                  fontSize: "20px",
                  color: theme.textPrimary,
                  letterSpacing: "-0.6px",
                  lineHeight: "none",
                  whiteSpace: "nowrap",
                }}
              >
                Rating
              </Text>
            </Grid>
            <Grid item xs={4}>
              <Text
                sx={{
                  fontWeight: 600,
                  fontSize: "20px",
                  color: theme.textPrimary,
                  letterSpacing: "-0.6px",
                  lineHeight: "none",
                  whiteSpace: "nowrap",
                }}
              >
                Remarks
              </Text>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Category cards */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          gap: { xs: "40px", md: "24px" },
          filter: shouldShowOverlay ? "blur(4px)" : "none",
          transition: "filter 0.2s ease",
        }}
      >
        {isMobile && (
          <Box sx={{ width: "100%" }}>
            <Text
              sx={{
                fontWeight: 400,
                fontSize: 16,
                color: "#2977EC",
                lineHeight: "24px",
                mb: "4px",
              }}
            >
              Performance Parameters
            </Text>
            <Text
              sx={{
                fontWeight: 400,
                fontSize: 16,
                color: theme.mobileLabelColor,
                lineHeight: "24px",
              }}
            >
              Audit of portfolio health across profiling, risk, and liquidity.
            </Text>
          </Box>
        )}
        {categories.map((key) =>
          isMobile ? (
            <MobileParameterCard key={key} title={key} rows={data[key]} />
          ) : (
            <CategoryCard key={key} title={key} rows={data[key]} />
          ),
        )}
      </Box>

      {/* Blur / lock overlay */}
      {shouldShowOverlay && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: { xs: "30%", sm: "35%", md: "35%" },
            width: { xs: "135px", sm: "120px", md: "250px" },
            height: "auto",
            zIndex: 10,
          }}
        >
          <OverlayComponent
            blur={componentState === "blur"}
            locked={componentState === "locked"}
          />
        </Box>
      )}
    </Box>
  );
};

export default ParameterRating;
